import React from "react";
import { SpriteThumb } from "../../../components/current-cell-info/SpriteThumb";
import { productBlueprintFor } from "../../../game/bench-work/blueprint";
import { FinishedProduct } from "../../../game/Materials";
import { Want } from "../../../game/story";
import { classNames } from "../../../utils/classNames";
import { formatMoney } from "../../../utils/formatNumber";
import { PIXELS_PER_CELL, PIXELS_PER_INCH } from "../../../views/shop-scale";

/**
 * A listing on her phone: the thing she wants, as the website sells it.
 * Drawn as the phone itself — a dark bezel around a bright screen with
 * a shop page on it: the product photo, the title, the price in a big
 * friendly size, a blurb, and a button nobody here is going to press.
 * Held up over the story's box while she shows it, near the height of
 * the screen — the listing is the whole point of her visit.
 *
 * The product photo is the game's own sprite of the piece, so when he
 * builds it, it is visibly the same thing — the joke is that hers
 * costs what it costs.
 */
export const PhoneListing: React.FC<{
  want: Want;
  /** Screen width in CSS pixels; everything inside scales with it. */
  width?: number;
  className?: string;
}> = ({ want, width = 208, className }) => {
  const scale = width / 208;
  const photo: FinishedProduct = {
    id: `listing-${want.id}`,
    type: want.productType,
    species: want.photoSpecies,
  };
  const blueprint = productBlueprintFor(want.productType);
  const fit = blueprint
    ? Math.max(blueprint.widthIn, blueprint.heightIn) * PIXELS_PER_INCH
    : PIXELS_PER_CELL;
  return (
    <div
      data-testid="phone-listing"
      data-want={want.id}
      className={classNames(
        "rounded-[1.6em] bg-ink-black p-[0.45em] shadow-[0_10px_24px_rgba(0,0,0,0.55)] ring-1 ring-white/15",
        className,
      )}
      style={{ width, fontSize: `${scale}rem` }}
    >
      <div className="overflow-hidden rounded-[1.2em] bg-white text-ink-black">
        {/* The browser's address strip */}
        <div className="flex items-center gap-[0.4em] bg-zinc-100 px-[0.8em] py-[0.35em] text-[0.55em] text-zinc-500">
          <span className="inline-block size-[0.9em] rounded-full border border-zinc-400" />
          <span className="flex-1 truncate rounded-full bg-white px-[0.8em] py-[0.15em]">
            shop.thefarmhousefinds.com
          </span>
        </div>
        {/* The product photo, on a showroom backdrop */}
        <div className="flex items-center justify-center bg-[#f1ece2] py-[0.6em]">
          <div className="rounded-[0.4em] bg-white/70 p-[0.3em] shadow-sm">
            <SpriteThumb material={photo} size={112 * scale} fit={fit} />
          </div>
        </div>
        <div className="space-y-[0.35em] px-[0.9em] pb-[0.9em] pt-[0.6em]">
          <div className="font-condensed text-[0.95em] font-bold uppercase leading-tight tracking-wide">
            {want.listingTitle}
          </div>
          <div className="font-condensed text-[0.6em] leading-snug text-zinc-600">
            {want.listingBlurb}
          </div>
          <div
            className="font-condensed text-[1.5em] font-bold tabular-nums leading-none text-emerald-800"
            data-testid="listing-price"
          >
            {formatMoney(want.listingPrice)}
          </div>
          <div className="rounded-full bg-amber-400 py-[0.35em] text-center font-condensed text-[0.7em] font-bold uppercase tracking-[0.15em] text-ink-black">
            Add to cart
          </div>
        </div>
      </div>
    </div>
  );
};
